import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ReferralStatsCard from './ReferralStatsCard';
import RewardStatusCard from './RewardStatusCard';
import PayoutHistoryCard from './PayoutHistoryCard';
import CopyReferralLinkButton from './CopyReferralLinkButton';
import SocialShareButtons from './SocialShareButtons';
import {
  Gift,
  Loader2,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  TrendingUp,
  Users,
  Banknote,
  Link2,
  Copy,
  Check,
  MousePointerClick,
  Activity,
  UserPlus,
  Calendar,
  ArrowUpRight,
} from 'lucide-react';

const ReferralDashboardSection = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'earnings' | 'referrals'
  const [copiedCode, setCopiedCode] = useState(null);

  const [referredUsers, setReferredUsers] = useState([]);
  const [referredUsersTotal, setReferredUsersTotal] = useState(0);

  useEffect(() => {
    fetchStats();
    fetchReferrals();
    fetchReferredUsers();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/my-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Failed to load referral stats');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferredUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/my-referred-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferredUsers(data.referred_users || []);
        setReferredUsersTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch referred users:', err);
    }
  };

  const fetchReferrals = async () => {
    setReferralsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/referrals/my-referrals?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReferrals(data.referrals || []);
      }
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
    } finally {
      setReferralsLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="outline" onClick={fetchStats}>
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referral-dashboard-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Gift className="w-5 h-5 text-violet-400" />
            Invite & Earn
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Share your referral link and earn 10% of donations as rewards
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-violet-500 text-violet-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('referrals')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'referrals'
              ? 'border-violet-500 text-violet-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Link2 className="w-4 h-4 inline mr-2" />
          My Referral Codes
        </button>
        <button
          onClick={() => setActiveTab('earnings')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'earnings'
              ? 'border-violet-500 text-violet-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Banknote className="w-4 h-4 inline mr-2" />
          Earnings & Payouts
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Stats Card */}
          <ReferralStatsCard
            clicks={stats?.clicks || 0}
            conversions={stats?.conversions || 0}
            referredUsers={referredUsersTotal}
            totalRevenue={stats?.total_revenue_generated || 0}
            rewardEligible={stats?.reward_eligible || false}
            rewardIssued={stats?.reward_issued || false}
            rewardThreshold={stats?.reward_threshold || 500}
          />

          {/* Referral Activity Table */}
          <div className="bg-card rounded-xl p-5 border border-border" data-testid="referral-activity-section">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              Referral Activity
            </h3>
            {referrals.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="pb-3 pr-4 font-medium">Code</th>
                      <th className="pb-3 pr-4 font-medium">Type</th>
                      <th className="pb-3 pr-4 font-medium">Linked To</th>
                      <th className="pb-3 pr-4 font-medium text-center">Clicks</th>
                      <th className="pb-3 pr-4 font-medium text-center">Conversions</th>
                      <th className="pb-3 pr-4 font-medium text-right">Revenue</th>
                      <th className="pb-3 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((ref) => (
                      <tr key={ref.id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                        <td className="py-3 pr-4">
                          <code className="text-xs bg-secondary px-2 py-1 rounded font-mono text-foreground">
                            {ref.referral_code}
                          </code>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline" className={
                            ref.referral_type === 'campaign' ? 'border-emerald-500/30 text-emerald-400' :
                            ref.referral_type === 'team' ? 'border-violet-500/30 text-violet-400' :
                            'border-blue-500/30 text-blue-400'
                          }>
                            {ref.referral_type}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground text-xs max-w-[180px] truncate">
                          {ref.campaign_title || ref.team_page_title || 'Personal referral'}
                        </td>
                        <td className="py-3 pr-4 text-center font-medium text-foreground">
                          {ref.clicks_count || 0}
                        </td>
                        <td className="py-3 pr-4 text-center font-medium text-foreground">
                          {ref.conversions_count || 0}
                        </td>
                        <td className="py-3 pr-4 text-right font-medium text-emerald-400">
                          {formatCurrency(ref.total_revenue_generated || 0)}
                        </td>
                        <td className="py-3 text-muted-foreground text-xs">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6">
                <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No referral activity yet</p>
                <p className="text-xs text-muted-foreground mt-1">Share your referral link to start tracking activity</p>
              </div>
            )}
          </div>

          {/* Referred Users List */}
          <div className="bg-card rounded-xl p-5 border border-border" data-testid="referred-users-section">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-cyan-400" />
              Referred Users
              {referredUsersTotal > 0 && (
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 ml-1">{referredUsersTotal}</Badge>
              )}
            </h3>
            {referredUsers.length > 0 ? (
              <div className="space-y-3">
                {referredUsers.map((user, index) => (
                  <div key={user.id || index} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                    <img
                      src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&size=32&background=0891b2&color=fff`}
                      alt={user.name || 'User'}
                      className="w-8 h-8 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{user.name || 'Anonymous User'}</p>
                      <p className="text-xs text-muted-foreground">
                        Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'recently'}
                      </p>
                    </div>
                    <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-xs">
                      <ArrowUpRight className="w-3 h-3 mr-1" /> Signed up
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <UserPlus className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No referred users yet</p>
                <p className="text-xs text-muted-foreground mt-1">When someone signs up using your referral link, they'll appear here</p>
              </div>
            )}
          </div>

          {/* Referral Link & Share */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-card rounded-xl p-5 border border-border">
              <CopyReferralLinkButton
                referralCode={stats?.personal_referral_code || ''}
              />
            </div>

            <div className="bg-card rounded-xl p-5 border border-border">
              <SocialShareButtons
                url={`${window.location.origin}/?ref=${stats?.personal_referral_code || ''}`}
                title="Support amazing causes on FundTheHope"
                description="Join me in making a difference! Donate to causes that matter."
              />
            </div>
          </div>

          {/* Top Campaigns */}
          {stats?.top_campaigns?.length > 0 && (
            <div className="bg-card rounded-xl p-5 border border-border">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                Top Performing Campaigns
              </h3>
              <div className="space-y-3">
                {stats.top_campaigns.map((campaign, index) => (
                  <Link
                    key={campaign.campaign_id}
                    to={`/campaign/${campaign.campaign_id}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <span className="text-lg font-bold text-muted-foreground w-6">#{index + 1}</span>
                    {campaign.image && (
                      <img
                        src={campaign.image}
                        alt={campaign.title}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{campaign.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {campaign.conversions} conversions
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-400">{formatCurrency(campaign.revenue)}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Top Team Pages */}
          {stats?.top_team_pages?.length > 0 && (
            <div className="bg-card rounded-xl p-5 border border-border">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-violet-400" />
                Top Team Fundraisers
              </h3>
              <div className="space-y-3">
                {stats.top_team_pages.map((teamPage, index) => (
                  <Link
                    key={teamPage.team_page_id}
                    to={`/fundraise/${teamPage.slug}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <span className="text-lg font-bold text-muted-foreground w-6">#{index + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{teamPage.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {teamPage.conversions} conversions
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-400">{formatCurrency(teamPage.revenue)}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* How It Works */}
          <div className="bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border border-violet-500/30 rounded-xl p-5">
            <h3 className="font-semibold text-foreground mb-3">How Referrals Work</h3>
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                Share your unique referral link with friends and family
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                When they donate through your link, we track the referral
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                Earn 10% of donations as rewards (paid after 14-day hold)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center text-xs font-bold shrink-0">4</span>
                Once you reach ${(stats?.reward_threshold || 500)} in rewards, payouts unlock
              </li>
            </ol>
          </div>
        </>
      )}

      {activeTab === 'referrals' && (
        <div className="space-y-6" data-testid="my-referrals-list">
          <div className="bg-card rounded-xl p-5 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Link2 className="w-5 h-5 text-violet-400" />
                My Referral Codes
              </h3>
              <Button variant="outline" size="sm" onClick={fetchReferrals} disabled={referralsLoading}>
                <RefreshCw className={`w-4 h-4 ${referralsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {referralsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              </div>
            ) : referrals.length === 0 ? (
              <div className="text-center py-8">
                <Link2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-2">No referral codes generated yet</p>
                <p className="text-sm text-muted-foreground">
                  Share your personal referral link or create campaign-specific codes to start tracking!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrals.map((referral) => (
                  <div
                    key={referral.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg bg-secondary/50 border border-border"
                    data-testid={`referral-item-${referral.referral_code}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-sm font-mono bg-background px-2 py-1 rounded text-violet-400">
                          {referral.referral_code}
                        </code>
                        <button
                          onClick={() => copyCode(referral.referral_code)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          title="Copy code"
                        >
                          {copiedCode === referral.referral_code ? (
                            <Check className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <Badge variant="outline" className="text-xs">
                          {referral.referral_type || 'donor'}
                        </Badge>
                      </div>
                      
                      {referral.campaign_title && (
                        <p className="text-sm text-muted-foreground">
                          Campaign: <span className="text-foreground">{referral.campaign_title}</span>
                        </p>
                      )}
                      {referral.team_page_title && (
                        <p className="text-sm text-muted-foreground">
                          Team Page: <span className="text-foreground">{referral.team_page_title}</span>
                        </p>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-1">
                        Created: {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MousePointerClick className="w-3 h-3" />
                          <span>{referral.clicks_count || 0}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Clicks</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Users className="w-3 h-3" />
                          <span>{referral.conversions_count || 0}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Conversions</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-emerald-400">
                          {formatCurrency(referral.total_revenue_generated || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Revenue</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card rounded-xl p-5 border border-border">
            <h3 className="font-semibold text-foreground mb-3">Generate New Codes</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create campaign-specific or team page referral codes to track performance for different fundraising efforts.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard?tab=all">
                  Go to Campaigns
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'earnings' && (
        <div className="space-y-6">
          {/* Reward Status Card */}
          <RewardStatusCard />

          {/* Payout History */}
          <PayoutHistoryCard />

          {/* Payout Info */}
          <div className="bg-card rounded-xl p-5 border border-border">
            <h3 className="font-semibold text-foreground mb-3">Payout Information</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5" />
                <p><strong>10% Reward Rate:</strong> You earn 10% of every donation made through your referral link.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5" />
                <p><strong>14-Day Hold:</strong> Rewards are held for 14 days to protect against chargebacks.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5" />
                <p><strong>$500 Minimum:</strong> First payout unlocks after reaching $500 in rewards.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5" />
                <p><strong>$2,000 Monthly Cap:</strong> Maximum payout per month is $2,000.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5" />
                <p><strong>Stripe Connect Required:</strong> You must connect your bank account via Stripe to receive payouts.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralDashboardSection;
